import React, { useState, useEffect } from 'react';
import { AppBar, Box, IconButton, Toolbar, Typography, Menu, MenuItem } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoutIcon from '@mui/icons-material/Logout';
import CompanySearch from './companySearch/CompanySearch';
import { CompanySearchResult } from '../../types/company';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, loginRequest, silentRequest } from '../../config/msalConfig';
import { useLanguage } from '../context/LanguageContext';
import { FormattedMessage } from 'react-intl';

const Header = () => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [msalInstance] = useState<PublicClientApplication>(() => new PublicClientApplication(msalConfig));
	const { setLanguage } = useLanguage();

	// Set Finnish as the only language
	useEffect(() => {
		setLanguage('fi');
	}, [setLanguage]);

	useEffect(() => {
		// Initialize MSAL instance
		msalInstance.initialize().catch((error: unknown) => {
			console.error('Error initializing MSAL:', error);
		});
	}, [msalInstance]);

	const handleCompanySelect = (_company: CompanySearchResult) => {
		//TODO: Handle the selected company
	};

	const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleRefreshToken = async () => {
		try {
			const account = msalInstance.getActiveAccount();
			if (!account) {
				await msalInstance.loginPopup(loginRequest);
				return;
			}

			const response = await msalInstance.acquireTokenSilent({
				...silentRequest,
				account: account,
			});

			handleMenuClose();
		} catch (error) {
			console.error('Error refreshing token:', error);
			// If silent token acquisition fails, try interactive
			try {
				const _ = await msalInstance.acquireTokenPopup(loginRequest);
			} catch (popupError) {
				console.error('Error getting token via popup:', popupError);
			}
		}
		handleMenuClose();
	};

	const handleClearToken = () => {
		localStorage.removeItem('granted_ai_token');
		localStorage.removeItem('granted_ai_token_expiry');
		handleMenuClose();
	};

	const handleSignOut = async () => {
		try {
			await msalInstance.logoutPopup();
			handleClearToken();
		} catch (error) {
			console.error('Error signing out:', error);
		}
		handleMenuClose();
	};

	return (
		<>
			<AppBar position='static'>
				<Toolbar>
					<IconButton edge='start' color='inherit' aria-label='menu' sx={{ mr: 2 }} onClick={handleMenuClick}>
						<MenuIcon />
					</IconButton>
					<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
						<MenuItem onClick={handleRefreshToken}>
							<RefreshIcon sx={{ mr: 1 }} color='primary' />
							<FormattedMessage id='menu.refreshToken' />
						</MenuItem>
						<MenuItem onClick={handleSignOut}>
							<LogoutIcon sx={{ mr: 1 }} color='primary' />
							<FormattedMessage id='menu.clearToken' />
						</MenuItem>
					</Menu>
					<Typography variant='h6' component='div' sx={{ mr: 2 }}>
						<FormattedMessage id='app.title' />
					</Typography>
					<Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
						<Box sx={{ flex: 2 }}>
							<CompanySearch onChange={handleCompanySelect} />
						</Box>
					</Box>
				</Toolbar>
			</AppBar>
		</>
	);
};

export default Header;
